import {createAction, createReducer} from '@reduxjs/toolkit'
import {takeLatest, getContext, put, call, race, take, select} from 'redux-saga/effects'
import Api, {UnauthorizedError} from 'Api'
import {RootState} from 'redux/rootReducer'
import {actions as searchActions} from './search'

// Plain JSON shape as returned by the API — not the full Device/Patient model classes,
// which add behavior (validation, formatting) this read-only lookup view doesn't need, and
// assignedPatient doesn't carry every field Patient's constructor requires (e.g. birthdate).
export type DeviceDetail = {
  id: string
  udid: string
  name: string
  status: string | null
  assignedPatient: {id: string; firstName: string; lastName: string} | null
  inventoryStatus: 'assigned' | 'in_inventory'
  assignedDate: string | null
  lastSync: string | null
}

// The only two transitions ALLOWED_DEVICE_LIFECYCLE_TRANSITIONS (theragen-api) currently
// permits — tightened from a plain string so a typo here is a compile error, not a silent
// 400 from the API.
export type DeviceTransitionStatus = 'replaced' | 'lost'

export type DeviceTimelineEntryType = 'inventory_added' | 'assignment' | 'status_transition'

export type DeviceTimelineEntry = {
  date: string
  type: DeviceTimelineEntryType
  description: string
  patientId?: string
  fromStatus?: string
  toStatus?: string
}

export type DeviceLookupState = {
  // Split detail/timeline loading and error state: the two fetches are independent, so a
  // slow or failed timeline fetch can't block or misattribute itself to the detail view.
  detail: DeviceDetail | undefined
  detailLoading: boolean
  detailError: string | undefined
  // Which udid the *current* getDetail request was issued for, set as soon as it's dispatched
  // (not when it resolves) — lets the component tell a genuinely-in-flight/failed fetch for the
  // device currently being viewed apart from a stale detailLoading/detailError left over from a
  // previous device, in the one render between a udid prop change and that new device's own
  // getDetail dispatch actually landing (detail.udid itself only covers the success case).
  detailRequestUdid: string | null
  timeline: DeviceTimelineEntry[]
  // Which udid `timeline` was fetched for — DeviceTimelineEntry itself carries no udid (unlike
  // DeviceDetail, which already has one), so this is the only way for the component to tell
  // "this cached timeline belongs to the device currently being viewed" apart from "this
  // component instance got reused for a different device without an intervening clear()" (the
  // parent can swap the udid prop while the modal stays mounted; clear() only fires on Close).
  timelineUdid: string | null
  timelineLoading: boolean
  timelineError: string | undefined
  // Same rationale as detailRequestUdid, for the timeline fetch.
  timelineRequestUdid: string | null
  // Kept separate from detailLoading/detailError for the same reason detail and timeline are
  // split from each other: a transition failing shouldn't be misattributed to the detail fetch,
  // and a successful transition's own refetch of detail has its own loading state.
  // Which toStatus is currently in flight (or null) rather than a plain boolean, so the UI can
  // show a spinner on the specific button that was clicked while still disabling both — a
  // shared boolean made every action button look "in progress" no matter which one was clicked.
  pendingToStatus: DeviceTransitionStatus | null
  // Which device pendingToStatus belongs to — without this, a transition still in flight for a
  // device the modal has since moved away from would disable/spin the newly-viewed device's own
  // action buttons (they share the same pendingToStatus scalar with no udid of their own).
  pendingUdid: string | null
  transitionError: string | undefined
  // Same rationale as pendingUdid: which device transitionError belongs to, so a failed
  // transition for a device no longer being viewed doesn't surface its error under a different
  // device's Actions section.
  transitionErrorUdid: string | null
}

export const initialState: DeviceLookupState = {
  detail: undefined,
  detailLoading: false,
  detailError: undefined,
  detailRequestUdid: null,
  timeline: [],
  timelineUdid: null,
  timelineLoading: false,
  timelineError: undefined,
  timelineRequestUdid: null,
  pendingToStatus: null,
  pendingUdid: null,
  transitionError: undefined,
  transitionErrorUdid: null,
}

export const actions = {
  getDetail: createAction<{udid: string}>('deviceLookup/getDetail'),
  setDetail: createAction<{detail: DeviceDetail}>('deviceLookup/setDetail'),
  setDetailLoading: createAction<{loading: boolean}>('deviceLookup/setDetailLoading'),
  setDetailError: createAction<{error?: string}>('deviceLookup/setDetailError'),
  getTimeline: createAction<{udid: string}>('deviceLookup/getTimeline'),
  setTimeline: createAction<{timeline: DeviceTimelineEntry[]; udid: string}>(
    'deviceLookup/setTimeline',
  ),
  setTimelineLoading: createAction<{loading: boolean}>('deviceLookup/setTimelineLoading'),
  setTimelineError: createAction<{error?: string}>('deviceLookup/setTimelineError'),
  // udid is carried alongside id so the saga can refetch detail/timeline (both keyed by udid)
  // after a successful transition, while the transition call itself needs the device's id.
  transition: createAction<{
    id: string
    udid: string
    toStatus: DeviceTransitionStatus
    newDeviceUdid?: string
    newDeviceName?: string
  }>('deviceLookup/transition'),
  setPendingToStatus: createAction<{toStatus: DeviceTransitionStatus | null; udid: string | null}>(
    'deviceLookup/setPendingToStatus',
  ),
  setTransitionError: createAction<{error?: string; udid: string | null}>(
    'deviceLookup/setTransitionError',
  ),
  // Not setTransitionError({error: undefined}): that also nulls pendingToStatus/pendingUdid.
  clearTransitionError: createAction('deviceLookup/clearTransitionError'),
  clear: createAction('deviceLookup/clear'),
}

export const reducer = createReducer(initialState, builder => {
  builder
    .addCase(actions.getDetail, (state, action) => {
      state.detailRequestUdid = action.payload.udid
    })
    .addCase(actions.getTimeline, (state, action) => {
      state.timelineRequestUdid = action.payload.udid
    })
    .addCase(actions.setDetail, (state, action) => {
      state.detail = action.payload.detail
      state.detailLoading = false
      state.detailError = undefined
    })
    .addCase(actions.setDetailLoading, (state, action) => {
      state.detailLoading = action.payload.loading
    })
    .addCase(actions.setDetailError, (state, action) => {
      state.detailError = action.payload.error
      state.detailLoading = false
    })
    .addCase(actions.setTimeline, (state, action) => {
      state.timeline = action.payload.timeline
      state.timelineUdid = action.payload.udid
      state.timelineLoading = false
      state.timelineError = undefined
    })
    .addCase(actions.setTimelineLoading, (state, action) => {
      state.timelineLoading = action.payload.loading
    })
    .addCase(actions.setTimelineError, (state, action) => {
      state.timelineError = action.payload.error
      state.timelineLoading = false
    })
    .addCase(actions.setPendingToStatus, (state, action) => {
      state.pendingToStatus = action.payload.toStatus
      state.pendingUdid = action.payload.udid
      // Starting a new attempt clears any error from a previous one, the same way
      // setDetailLoading doesn't on its own but setDetail/setDetailError do for their pair.
      if (action.payload.toStatus !== null) {
        state.transitionError = undefined
        state.transitionErrorUdid = null
      }
    })
    .addCase(actions.setTransitionError, (state, action) => {
      state.transitionError = action.payload.error
      state.transitionErrorUdid = action.payload.udid
      // Defensive, matching setDetailError/setTimelineError's own pattern: whoever sets the
      // error is also responsible for clearing the corresponding pending state, so a future
      // caller of setTransitionError can't leave the buttons stuck disabled.
      state.pendingToStatus = null
      state.pendingUdid = null
    })
    .addCase(actions.clearTransitionError, state => {
      state.transitionError = undefined
      state.transitionErrorUdid = null
    })
    .addCase(actions.clear, () => initialState)
})

const getDetailSaga = function*(action: ReturnType<typeof actions.getDetail>) {
  yield put(actions.setDetailLoading({loading: true}))
  const api: Api = yield getContext('api')
  try {
    const detail: DeviceDetail = yield call(api.getDeviceDetail, action.payload.udid)
    yield put(actions.setDetail({detail}))
  } catch (e) {
    const error = e as any
    if (error === UnauthorizedError) throw error
    yield put(actions.setDetailError({error: error?.message ?? 'Error loading device detail'}))
  }
}

const getTimelineSaga = function*(action: ReturnType<typeof actions.getTimeline>) {
  yield put(actions.setTimelineLoading({loading: true}))
  const api: Api = yield getContext('api')
  try {
    const timeline: DeviceTimelineEntry[] = yield call(api.getDeviceTimeline, action.payload.udid)
    yield put(actions.setTimeline({timeline, udid: action.payload.udid}))
  } catch (e) {
    const error = e as any
    if (error === UnauthorizedError) throw error
    yield put(
      actions.setTimelineError({error: error?.message ?? 'Error loading device timeline'}),
    )
  }
}

const transitionSaga = function*(action: ReturnType<typeof actions.transition>) {
  const {id, udid, toStatus, newDeviceUdid, newDeviceName} = action.payload
  yield put(actions.setPendingToStatus({toStatus, udid}))
  const api: Api = yield getContext('api')
  try {
    const response: {status: string} = yield call(
      api.transitionDevice,
      id,
      toStatus,
      newDeviceUdid,
      newDeviceName,
    )
    // The Devices grid's Status column reads the search slice, not this modal's own state —
    // without this it keeps showing the pre-transition status until the admin re-runs the search.
    yield put(searchActions.refreshSearch())
    // Apply the new status locally before the refetch below settles: the transition already
    // succeeded server-side at this point, so the modal shouldn't depend on a second, separate
    // network call to confirm that (a refetch failure here must not leave the UI showing the
    // stale pre-transition status/Actions, which would invite a duplicate transition attempt).
    // Uses the server's own response.status rather than the locally-held toStatus, so a
    // hypothetical future backend normalization wouldn't be silently overridden by the request
    // we sent. The other fields (assignedPatient/inventoryStatus/assignedDate) are patched to
    // match what GET .../detail deterministically reports once an assignment is deactivated
    // (DeviceController.getDetail: no activeAssignment left, so assignedPatient/assignedDate
    // fall through to null and inventoryStatus becomes 'in_inventory') — otherwise, if the
    // refetch below fails, the panel would show the new status chip next to a stale patient
    // name and an "In Use" inventory tile that no longer matches.
    const currentDetail: DeviceDetail | undefined = yield select(
      (state: RootState) => state.deviceLookup.detail,
    )
    // Guard against the component having been reused for a *different* device while this
    // transition was in flight: takeLatest only cancels on a new `transition` action, not on
    // getDetail/getTimeline for another udid, so without this check device B's displayed detail
    // could get device A's resolved status/assignedPatient/inventoryStatus merged onto it.
    if (currentDetail && currentDetail.udid === udid) {
      yield put(
        actions.setDetail({
          detail: {
            ...currentDetail,
            status: response.status,
            assignedPatient: null,
            inventoryStatus: 'in_inventory',
            assignedDate: null,
          },
        }),
      )
    }
    // Still refetch for the authoritative shape (in particular the new AuditEvent-backed
    // timeline entry) — this is a best-effort refresh on top of the optimistic update above,
    // not the sole source of "did it work". Safe to do immediately: the transition committed
    // in a single DB transaction before the POST above resolved, and detail/timeline are read
    // from the same Postgres instance (no replica lag), so this can't race the write.
    // Guarded the same way as the optimistic patch above, but against detailRequestUdid/
    // timelineRequestUdid rather than detail/timelineUdid: if the modal has moved on to a
    // different device, that device already dispatched its own getDetail/getTimeline, which set
    // these to its own udid. Refetching this (no-longer-displayed) device anyway would stomp
    // that tracking back to this udid and, once it resolved, overwrite the other device's
    // displayed detail/timeline with this one's stale response — neither takeLatest (only
    // cancels on a new action of the *same* type) nor the render-layer udid guards protect
    // against a dispatch that was never supposed to happen in the first place.
    const detailRequestUdid: string | null = yield select(
      (state: RootState) => state.deviceLookup.detailRequestUdid,
    )
    if (detailRequestUdid === udid) {
      yield put(actions.getDetail({udid}))
    }
    const timelineRequestUdid: string | null = yield select(
      (state: RootState) => state.deviceLookup.timelineRequestUdid,
    )
    if (timelineRequestUdid === udid) {
      yield put(actions.getTimeline({udid}))
    }
  } catch (e) {
    const error = e as any
    if (error === UnauthorizedError) throw error
    yield put(
      actions.setTransitionError({error: error?.message ?? 'Error updating device status', udid}),
    )
  } finally {
    // Always clears pendingToStatus, including when the catch block above rethrows for
    // UnauthorizedError — without this, a session expiring mid-transition would leave the
    // action buttons disabled (state is redux-persist'd) until the modal happened to be closed.
    yield put(actions.setPendingToStatus({toStatus: null, udid: null}))
  }
}

// Races each fetch against `clear()` so closing the modal (or `clear()` firing for any other
// reason) cancels a still-in-flight request — without this, a late response would still call
// setDetail/setTimeline and repopulate state.deviceLookup after the user already dismissed it.
const cancellableOnClear = function*(saga: (action: any) => Generator, action: any) {
  yield race([call(saga, action), take(actions.clear)])
}

export const saga = function*() {
  // takeLatest cancels a still-in-flight fetch when a newer one is dispatched, so a stale
  // response from a previously-looked-up device can never overwrite the currently-open one.
  yield takeLatest(actions.getDetail, cancellableOnClear, getDetailSaga)
  yield takeLatest(actions.getTimeline, cancellableOnClear, getTimelineSaga)
  yield takeLatest(actions.transition, cancellableOnClear, transitionSaga)
}
